// External Imports
import { useState, useEffect, useContext } from "react";
import { MdClose } from "react-icons/md";

// Internal Imports
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import { toTitleCase } from "../../util/format-text";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import Modal from "../UI/Modal";
import PersonCard from "../UI/PersonCard";
import VehicleInformationCard from "../UI/VehicleInformationCard";
import ImageSelector from "../UI/ImageSelector";

/** ========================================================================
 * * Toll Information Modal
 * Renders a modal, which displays all information about a toll.
 * ========================================================================== */

export default function TollInfo({ open, setOpen, tollId }) {
  // Load Context
  const { navigate } = useContext(UtilityContext);

  // Initialize States
  const [toll, setToll] = useState({});
  const [loading, setLoading] = useState(true);

  // =================== FETCH RECORD ===================
  const fetchRecord = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "toll",
        id: tollId,
        columns: [
          "vehicle { id year make { name } model vin license cornerImage }",
          "booking { id startDate endDate status customer { id firstName lastName phone photo } }",
        ],
      });
      setToll({ ...response, customer: response.booking?.customer });
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== USE_EFFECT ===================
  useEffect(() => {
    if (tollId && open) fetchRecord();
  }, [open, tollId]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal max-w-[800px]">
        <Heading title="Toll Information" id={tollId} isBackArrow={false}>
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>

        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Date & Time"
            value={isoDateToString(toll.date, "eee dd MMM, yyyy - hh:mm a")}
            loading={loading}
          />
          <DetailPair
            label="Amount"
            classes={"font-bold text-lg"}
            value={`$${toll.amount?.toFixed(2)}`}
            loading={loading}
          />
          <DetailPair
            label="Status"
            value={toll.invoiced ? "INVOICED" : "NOT INVOICED"}
            classes={`${
              toll.invoiced ? "text-green-600" : "text-red-600"
            } font-semibold text-lg`}
            loading={loading}
          />

          <DetailPair label="Agency" value={toll.agency} loading={loading} />
          <div className="lg:col-span-2">
            <DetailPair
              label="Location"
              value={toll.location}
              loading={loading}
            />
          </div>

          <DetailPair
            label="Plate"
            value={`${toll.license || toll.vehicle?.license || ""} ${
              toll.state || ""
            }`}
            loading={loading}
          />

          <DetailPair
            label="Invoice ID"
            onClick={(e) =>
              toll.invoiceId &&
              navigate(`/payments/info?id=${toll.invoiceId}`, e)
            }
            value={toll.invoiceId}
            loading={loading}
            classes={toll.invoiceId ? "clickable text-blue-500" : ""}
          />
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mt-6 text-sm">
          {toll.vehicle && (
            <VehicleInformationCard
              label="BOOKING"
              name={`${toll.vehicle.year} ${
                toll.vehicle.model
              } ${toll.vehicleId.substring(13, 17)}`}
              secondary={`${isoDateToString(
                toll.booking.startDate,
                "MM/dd/yyyy"
              )} - ${isoDateToString(toll.booking.endDate, "MM/dd/yyyy")}`}
              image={toll.vehicle.cornerImage}
              loading={loading}
              link={`/booking/info?id=${toll.booking.id}`}
            />
          )}

          {toll.customer && (
            <PersonCard
              link={`/customers/info?id=${toll.customerId}`}
              label="CUSTOMER"
              name={`${toll.customer?.firstName} ${toll.customer?.lastName}`}
              secondary={toll.customer?.phone}
              image={toll.customer?.photo}
              loading={loading}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
